import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  ERoomType,
  EXCLUSIVE_GROUP_LABELS,
  PhotoGenerationConfig,
  ROOM_OPTIONS,
  ROOM_TYPE_OPTIONS,
  RoomOptionDef,
} from '../../models/wizard.model';

export interface RoomOptionGroup {
  id: string;
  label: string;
  options: RoomOptionDef[];
}

@Component({
  selector: 'casa-doc-photo-advanced-panel',
  standalone: true,
  imports: [NgClass],
  templateUrl: './photo-advanced-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoAdvancedPanelComponent {
  config = input.required<PhotoGenerationConfig>();

  configChange = output<PhotoGenerationConfig>();

  readonly roomTypes = ROOM_TYPE_OPTIONS;

  /** Emoji per il tipo di stanza, per rendere la scelta immediata. */
  readonly roomIcons: Record<ERoomType, string> = {
    [ERoomType.LIVING]: '🛋️',
    [ERoomType.CUCINA]: '🍳',
    [ERoomType.CAMERA]: '🛏️',
    [ERoomType.BAGNO]: '🛁',
    [ERoomType.STUDIO]: '💻',
    [ERoomType.INGRESSO]: '🚪',
    [ERoomType.BALCONE]: '🪴',
  };

  /** Numero di opzioni stanza selezionate (per il badge dello step 2). */
  selectedCount = computed(() => this.config().selectedOptionIds.length);

  roomOptions = computed<RoomOptionDef[]>(() => {
    const roomType = this.config().roomType;
    return roomType ? ROOM_OPTIONS[roomType] : [];
  });

  /** Gruppi esclusivi della stanza corrente, renderizzati come select. */
  exclusiveGroups = computed<RoomOptionGroup[]>(() => {
    const groups: RoomOptionGroup[] = [];
    for (const option of this.roomOptions()) {
      if (!option.exclusiveGroup) continue;
      let group = groups.find((g) => g.id === option.exclusiveGroup);
      if (!group) {
        group = {
          id: option.exclusiveGroup,
          label: EXCLUSIVE_GROUP_LABELS[option.exclusiveGroup] ?? '',
          options: [],
        };
        groups.push(group);
      }
      group.options.push(option);
    }
    return groups;
  });

  /** Opzioni indipendenti della stanza corrente, renderizzate come checkbox. */
  standaloneOptions = computed<RoomOptionDef[]>(() =>
    this.roomOptions().filter((option) => !option.exclusiveGroup)
  );

  isSelected(optionId: string): boolean {
    return this.config().selectedOptionIds.includes(optionId);
  }

  selectedInGroup(group: RoomOptionGroup): string {
    const groupIds = group.options.map((option) => option.id);
    return (
      this.config().selectedOptionIds.find((id) => groupIds.includes(id)) ?? ''
    );
  }

  /** Seleziona la stanza; ri-cliccare quella attiva la deseleziona. */
  selectRoomType(value: ERoomType) {
    const next = this.config().roomType === value ? null : value;
    this.configChange.emit({
      ...this.config(),
      roomType: next,
      selectedOptionIds: [],
    });
  }

  /** Pillola di un gruppo esclusivo; ri-cliccare quella attiva la deseleziona. */
  selectGroupOption(group: RoomOptionGroup, optionId: string) {
    const groupIds = group.options.map((option) => option.id);
    const rest = this.config().selectedOptionIds.filter(
      (id) => !groupIds.includes(id)
    );
    const isActive = this.selectedInGroup(group) === optionId;
    this.configChange.emit({
      ...this.config(),
      selectedOptionIds: isActive ? rest : [...rest, optionId],
    });
  }

  onToggleOption(option: RoomOptionDef) {
    const current = this.config().selectedOptionIds;
    const next = current.includes(option.id)
      ? current.filter((id) => id !== option.id)
      : [...current, option.id];
    this.configChange.emit({ ...this.config(), selectedOptionIds: next });
  }

  onCustomPromptInput(event: Event) {
    this.configChange.emit({
      ...this.config(),
      customPrompt: (event.target as HTMLTextAreaElement).value,
    });
  }

  onToggleStructuralChanges(event: Event) {
    this.configChange.emit({
      ...this.config(),
      allowStructuralChanges: (event.target as HTMLInputElement).checked,
    });
  }
}
